import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GalleryAsset, ReorderGalleryItem } from '../../games/games-types';
import { useReorderGallery } from '../game-catalog-queries';

/**
 * v0.3.0 Game Catalog — gallery manager with drag-drop reorder.
 *
 * RT-R8: REPLACE debounce with immediate PATCH on drag-end. Backend
 * serializes concurrent reorders via `SELECT ... FOR UPDATE`.
 *
 * Tab-close safety: `beforeunload` flushes any in-flight reorder via
 * `navigator.sendBeacon`. Beacon sends a small JSON payload to the same
 * endpoint with method POST (sendBeacon only supports POST). When the user
 * closes the tab mid-reorder, the beacon ensures the latest state persists.
 */
export interface GalleryManagerProps {
  gameId: string;
  assets: GalleryAsset[];
  onAdd?: (file: File) => void;
  onRemove?: (assetId: string) => void;
}

export function GalleryManager({ gameId, assets, onAdd, onRemove }: GalleryManagerProps) {
  const [local, setLocal] = useState<GalleryAsset[]>(assets);
  const pendingReorder = useRef<ReorderGalleryItem[] | null>(null);
  const reorder = useReorderGallery(gameId);

  // Sync remote → local
  useEffect(() => {
    setLocal(assets);
  }, [assets]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setLocal((prev) => {
        const oldIndex = prev.findIndex((a) => a.id === active.id);
        const newIndex = prev.findIndex((a) => a.id === over.id);
        const next = arrayMove(prev, oldIndex, newIndex);
        const payload = next.map((a, i) => ({ id: a.id, display_order: i }));
        pendingReorder.current = payload;
        // RT-R8: immediate PATCH on drag-end. Backend SELECT FOR UPDATE serializes.
        reorder.mutate({ assets: payload });
        return next;
      });
    },
    [reorder],
  );

  // RT-R8: flush pending reorder on tab close via sendBeacon.
  useEffect(() => {
    const handler = () => {
      const payload = pendingReorder.current;
      if (!payload) return;
      const url = `/api/admin/games/${gameId}/gallery/reorder`;
      const blob = new Blob([JSON.stringify({ assets: payload })], {
        type: 'application/json',
      });
      navigator.sendBeacon(url, blob);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [gameId]);

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-medium">Gallery ({local.length})</h3>
        {onAdd && (
          <label className="px-3 py-1 bg-blue-600 text-white rounded cursor-pointer text-sm">
            + Upload image
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onAdd(file);
              }}
            />
          </label>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={local.map((a) => a.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-3 gap-3">
            {local.map((asset) => (
              <SortableGalleryCard
                key={asset.id}
                asset={asset}
                onRemove={onRemove}
              />
            ))}
            {local.length === 0 && (
              <p className="col-span-3 text-sm text-gray-500 italic">No gallery images yet.</p>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableGalleryCard({
  asset,
  onRemove,
}: {
  asset: GalleryAsset;
  onRemove?: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: asset.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative border rounded p-2 bg-white">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 px-2 py-1 bg-gray-100 rounded text-xs cursor-grab"
        aria-label="Drag to reorder"
      >
        ⇅
      </button>
      <img src={asset.url} alt="" className="w-full aspect-square object-cover" />
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(asset.id)}
          className="absolute top-1 right-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs"
        >
          ×
        </button>
      )}
    </div>
  );
}